import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All Projects" },
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" },
  { id: "interior", label: "Interior" },
];

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects?category=${activeCategory}`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, [activeCategory]);

  // Tetris grid class assignment
  const getGridClass = (index) => {
    const patterns = [
      "col-span-1 md:col-span-8 aspect-[16/9]",
      "col-span-1 md:col-span-4 aspect-[4/5]",
      "col-span-1 md:col-span-4 aspect-[4/5]",
      "col-span-1 md:col-span-4 aspect-[4/5]",
      "col-span-1 md:col-span-4 aspect-[4/5]",
      "col-span-1 md:col-span-6 aspect-[4/3]",
      "col-span-1 md:col-span-6 aspect-[4/3]",
    ];
    return patterns[index % patterns.length];
  };

  return (
    <div data-testid="projects-page" className="bg-[#F4F4F0] pt-24">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            Our Portfolio
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            Selected
            <br />
            works.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed max-w-2xl"
          >
            Explore our portfolio of residential, commercial, and interior design projects that showcase our commitment to excellence.
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div
          data-testid="project-filters"
          className="flex flex-wrap gap-4 md:gap-8"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              data-testid={`filter-${category.id}`}
              onClick={() => setActiveCategory(category.id)}
              className={`text-xs uppercase tracking-[0.2em] pb-2 transition-colors duration-300 ${
                activeCategory === category.id
                  ? "text-[#1A1A1A] border-b border-[#1A1A1A]"
                  : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </section>

      {/* Projects Grid - Tetris Style */}
      <section
        data-testid="projects-grid"
        className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
      >
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6"
        >
          <AnimatePresence mode="popLayout">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className={`${getGridClass(index)} group relative overflow-hidden cursor-pointer`}
                onClick={() => setSelectedProject(project)}
                data-testid={`project-card-${index}`}
              >
                <img
                  src={project.image_url}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/70 mb-2">
                    {project.category} • {project.year}
                  </p>
                  <h3 className="font-heading text-2xl md:text-3xl text-[#F4F4F0] mb-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-[#F4F4F0]/70">
                    {project.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {projects.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#1A1A1A]/50">No projects found in this category.</p>
          </div>
        )}
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={() => setSelectedProject(null)}
            data-testid="project-modal"
          >
            <div className="absolute inset-0 bg-[#1A1A1A]/90 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-[#F4F4F0] max-w-5xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedProject(null)}
                data-testid="close-modal-btn"
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-[#1A1A1A] text-[#F4F4F0] hover:bg-[#3A4D39] transition-colors"
              >
                ✕
              </button>
              <div className="aspect-video">
                <img
                  src={selectedProject.image_url}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 md:p-12">
                <div className="flex flex-wrap gap-4 mb-6">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 px-3 py-1 border border-[#1A1A1A]/20">
                    {selectedProject.category}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 px-3 py-1 border border-[#1A1A1A]/20">
                    {selectedProject.year}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 px-3 py-1 border border-[#1A1A1A]/20">
                    {selectedProject.location}
                  </span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] mb-6">
                  {selectedProject.title}
                </h2>
                <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
                  {selectedProject.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsPage;
